import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider } from "@/context/AppContext";
import Header from "@/components/Header";
import AgeGate from "@/components/AgeGate";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import EventDetail from "@/pages/EventDetail";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import MyEvents from "@/pages/MyEvents";
import CreateEvent from "@/pages/CreateEvent";
import OrganizerDashboard from "@/pages/OrganizerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminEventReview from "@/pages/AdminEventReview";
import AdminAudit from "@/pages/AdminAudit";
import EditEvent from "@/pages/EditEvent";
import ResetPassword from "@/pages/ResetPassword";
import SubmitGate from "@/pages/SubmitGate";
import Profile from "@/pages/Profile";
import Archive from "@/pages/Archive";
import OrganizerProfile from "@/pages/OrganizerProfile";
import UserProfile from "@/pages/UserProfile";
import { NotFound } from "@/pages/Static";
import { Privacy, Imprint, Terms, Guidelines } from "@/pages/Legal";
import About from "@/pages/About";
import Messages from "@/pages/Messages";
import Help from "@/pages/Help";
import Faq from "@/pages/Faq";
import Suspended from "@/pages/Suspended";
import AdminUsers from "@/pages/AdminUsers";
import CrewProfile from "@/pages/CrewProfile";
import AdminCrews from "@/pages/AdminCrews";
import AdminUserDetail from "@/pages/AdminUserDetail";
import { useApp } from "@/context/AppContext";

function AppShell() {
  const loc = useLocation();
  const { user } = useApp();
  // Detect OAuth callback via hash — sync during render (before ProtectedRoute)
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  const suspendedAllowed = ["/suspended", "/help", "/faq", "/about", "/privacy", "/imprint", "/terms", "/guidelines"];
  if (user?.suspended && !suspendedAllowed.some(p => loc.pathname.startsWith(p))) return <Navigate to="/suspended" replace />;
  return (
    <div className="App gg-grain min-h-screen flex flex-col">
      <Header />
      <AgeGate />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/map" element={<Home forcedView="map" />} />
          <Route path="/events/:slug" element={<EventDetail />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/my-events" element={<MyEvents />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/organizer" element={<OrganizerDashboard />} />
          <Route path="/dashboard" element={<OrganizerDashboard />} />
          <Route path="/organizers/:user_id" element={<OrganizerProfile />} />
          <Route path="/users/:user_id" element={<UserProfile />} />
          <Route path="/crews/:crew_id" element={<CrewProfile />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/audit" element={<AdminAudit />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/crews" element={<AdminCrews />} />
          <Route path="/admin/users/:user_id" element={<AdminUserDetail />} />
          <Route path="/admin/events/:event_id" element={<AdminEventReview />} />
          <Route path="/admin/events/:event_id/edit" element={<EditEvent admin />} />
          <Route path="/events/:event_id/edit" element={<EditEvent />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/submit" element={<SubmitGate />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/archive" element={<Archive />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/help" element={<Help />} />
          <Route path="/suspended" element={<Suspended />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:cid" element={<Messages />} />
          <Route path="/imprint" element={<Imprint />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/guidelines" element={<Guidelines />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </AppProvider>
  );
}
